import React, { useEffect, useRef, useState } from 'react'
import { withRouter } from 'react-router'
import JSZip from 'jszip'
import * as yup from 'yup'
import { ErrorMessage, Formik } from 'formik'
import { connect, useDispatch, useSelector } from 'react-redux'
import { Button, Dropdown, DropdownButton, FormControl, InputGroup } from 'react-bootstrap'
import { withGoogleReCaptcha } from 'react-google-recaptcha-v3'
import * as Sentry from '@sentry/react'

import {
  API_ROOT,
  DOMAIN_SUFFIX,
  ERROR_MSGS,
  MAX_FILE_SIZE,
  MAX_PDF_SIZE,
  REGEX_DOMAIN,
  errors,
  DROPZONE_ACCEPTED_FILES_PRO,
  MAX_FILE_SIZE_FREE,
  MIXED_FOLDER_ERROR_MSG,
  MIXED_ZIP_ERROR_MSG
} from '../constants'
import { STAGE_SUCCESS, STAGE_SUCCESS_VERIFY } from '../Landing/Home/Home'
import {
  createFileInput,
  getErrorMessage,
  isPDF,
  isTotalSizeWithinLimit,
  isValidFileSize,
  validateDisallowedEmails,
  isWasmerFile,
  onUpgradePlan,
  getFileFromIndexedDB,
  generateLinkName
} from '../utils/general'
import ContentSelector from './ContentSelector/ContentSelector'
import { showUpgradeCardModal } from '../actions'
import FileListModal from 'components/FileListModal/FileListModal'
import SocialLogin from './SocialLogin/SocialLogin'
import { getOAuthUrl } from '../services/auth'
import { setDraggedFile } from 'Manage/actions'
import {
  getPhpFiles,
  getPythonFiles,
  getNodeFiles,
  isHtmlFile,
  determineHtmlIndexFile,
  findHtmlFilesInZipFile,
  isIndexFile,
  isHtmlFileExisting
} from 'helpers/file-type'

import { getAddToCalHTML } from '../utils/AddToCal'
import { promptForAdditionalFiles, removeFileFromUploadState } from '../utils/file-upload'
import SelectedFile from './SelectedFile/SelectedFile'
import api from 'utils/api'
import { isZipFile } from './CreateUpdateSiteModal/interface'
import TINY_PLAN from 'constants/plans/tiny'
import { appendUtmParamsToFormData } from '../utils/utm'
import { classifierScan } from 'services/site'
import CodeEditor from './CodeEditor/CodeEditor'
import { isFolderDrop, zipFolderFiles, createMockFolderFile, zipFolderFilesWithWorker } from 'utils/folder-upload'
import toast from 'react-hot-toast'

import '../App.css'

const params = new URLSearchParams(window.location.search)

const additionalEmailChecks = (value) => {
  if (value) {
    return validateDisallowedEmails(value.toLowerCase())
  }
}

const cleanSubdomain = (value) => {
  if (!value) return value

  let cleaned = value.toLowerCase().replace(/[^a-z0-9-]/g, '')
  cleaned = cleaned.replace(/-+/g, '-')
  cleaned = cleaned.replace(/^-+|-+$/g, '')
  cleaned = cleaned.substring(0, 63)

  if (!cleaned || !REGEX_DOMAIN.test(cleaned)) {
    return ''
  }

  return cleaned
}

const schema = yup.object({
  fileSelected: yup.mixed(),
  email: yup
    .string()
    .when('fileSelected', (f, schema) =>
      f && f.isTemplate
        ? schema
        : schema
            .email("Oops, that's not a valid email")
            .test('email', 'Please enter a real email address', additionalEmailChecks)
            .required('Please enter an email')
    )
})

const HomepageUpload = ({ disabled, contentType, history, location, setRootState, googleReCaptchaProps }) => {
  const [showFileList, setShowFileList] = useState(false)
  const [htmlFiles, setHtmlFiles] = useState()
  const [phpFiles, setPhpFiles] = useState([])
  const [authToken, setAuthToken] = useState()
  const [domainSuffix, setDomainSuffix] = useState(DOMAIN_SUFFIX)
  const formRef = useRef()
  const draggedFile = useSelector((state) => state.manage.draggedFile)
  const dispatch = useDispatch()
  const [showCodeEditor, setShowCodeEditor] = useState(false)

  const openCodeEditor = () => {
    setShowCodeEditor(true)
    history.replace({ ...history.location, hash: '#paste' })
  }

  const closeCodeEditor = () => {
    setShowCodeEditor(false)
    history.replace({ ...history.location, hash: '' })
  }

  useEffect(() => {
    setShowCodeEditor(location.hash === '#paste')
  }, [location.hash])

  useEffect(() => {
    window.addEventListener('message', onMessageEvent)
    // on unmount
    return () => window.removeEventListener('message', onMessageEvent)
  }, [])

  useEffect(() => {
    if (draggedFile) {
      const files = Array.isArray(draggedFile) ? draggedFile : [draggedFile]
      const existingFiles = formRef.current.values.files || []
      const fileSelected = formRef.current.values.fileSelected

      const isReplacement = files.some((f) => f && f.isFolder)

      if (!isReplacement) {
        const allFiles = [...(fileSelected ? [fileSelected] : []), ...existingFiles, ...files].filter(Boolean)

        if (allFiles.some((file) => file && isZipFile(file.type)) && allFiles.length > 1) {
          if (allFiles.some((file) => file && file.isFolder)) {
            toast.error(MIXED_FOLDER_ERROR_MSG)
          } else {
            toast.error(MIXED_ZIP_ERROR_MSG)
          }
          dispatch(setDraggedFile(null))
          return
        }
      }

      onFileSelected(files, isReplacement).then(() => {
        dispatch(setDraggedFile(null))
      })
    }
  }, [draggedFile])

  const zipFileUploaded = async (file) => {
    const filesFound = await findHtmlFilesInZipFile(file)
    const { containsPhpFile, phpFiles } = await getPhpFiles(file)
    const { containsPythonFile, pythonFiles } = await getPythonFiles(file)
    const { containsNodeFile, isReact } = await getNodeFiles(file)

    if (containsNodeFile || isReact) {
      file.isNodeOrReactZip = true
      setDomainSuffix('.tiiny.io')
      setPhpFiles([])
      setHtmlFiles([])
      setShowFileList(false)
    } else if ((containsPhpFile && phpFiles?.length > 0) || (containsPythonFile && pythonFiles?.length > 0)) {
      setPhpFiles(containsPhpFile ? phpFiles : pythonFiles)
      setShowFileList(true)
    } else {
      // Only HTML files - use smart logic
      const { htmlFilesFound, shouldShowModal, indexFileToSet } = determineHtmlIndexFile(
        filesFound.map((f) => ({ name: f, path: f }))
      )

      setHtmlFiles(htmlFilesFound)
      setShowFileList(shouldShowModal)

      // Auto-set index if determined (single file or index.html exists)
      if (indexFileToSet) {
        formRef.current.setFieldValue('indexFile', indexFileToSet)
      }
    }
  }

  const isNewHtmlFilesUploaded = (files) => {
    // Only process if there are HTML files
    if (!isHtmlFileExisting(files)) {
      return
    }

    const { htmlFilesFound, shouldShowModal, indexFileToSet } = determineHtmlIndexFile(files)

    // Always update HTML files list
    setHtmlFiles(htmlFilesFound)

    // Show modal if needed
    if (shouldShowModal) {
      setShowFileList(true)
    }

    // Set index file if determined
    if (indexFileToSet) {
      formRef.current.setFieldValue('indexFile', indexFileToSet)
    } else if (htmlFilesFound.length === 0) {
      // Clear if no HTML files
      formRef.current.setFieldValue('indexFile', null)
    }
  }

  const onFileSelected = async (passedFiles, isReplacement = false) => {
    let setFieldValue = formRef.current.setFieldValue
    let files = passedFiles

    if (isFolderDrop(passedFiles)) {
      try {
        const zipFile = createMockFolderFile(passedFiles)
        files = [zipFile]
      } catch (error) {
        toast.error('Error processing the folder. Please try again.')
        return
      }
    }

    if (!files.length && !draggedFile) {
      alert(
        'We currently support html, pdf, php, and zip files.\n\nPlease Login or Sign up to upload images & documents.'
      )
      return
    }

    if (files.some((file) => file.path?.startsWith('/'))) {
      files = files.filter((file) => !file.path?.startsWith('/'))
    }

    if (files.some((file) => isZipFile(file.type)) && files.length > 1) {
      if (files.some((file) => file.isFolder)) {
        toast.error(MIXED_FOLDER_ERROR_MSG)
      } else {
        toast.error(MIXED_ZIP_ERROR_MSG)
      }
      return
    }

    const existingFiles = formRef.current.values.files || []
    const existingFileSelected = formRef.current.values.fileSelected

    for (const file of files) {
      if (!isValidFileSize(file, MAX_FILE_SIZE_FREE)) {
        dispatch(
          showUpgradeCardModal({
            title: 'Upgrade to upload larger files',
            desc: `${isPDF(file) ? 'PDFs' : 'Files'} should be less than ${
              isPDF(file) ? MAX_PDF_SIZE : MAX_FILE_SIZE
            }MB on our free plan. File "${file.name}" is too large.`,
            cta: 'Upload larger files',
            onClick: file?.size ? () => onUpgradePlan({ fileSize: file?.size }, dispatch) : null
          })
        )
        return
      }
    }

    if (!existingFileSelected || isReplacement) {
      const selectedFile = files[0]

      if (isZipFile(selectedFile.type)) {
        await zipFileUploaded(selectedFile)
      } else if (isWasmerFile(selectedFile)) {
        setDomainSuffix('.tiiny.io')
      }

      setFieldValue('fileSelected', selectedFile)
      setFieldValue('files', files.slice(1))
      setFieldValue('filesToAdd', files)

      if (!isHtmlFile(selectedFile.name)) {
        const linkName = generateLinkName(selectedFile, formRef.current.values.subdomain)
        const cleanedLinkName = cleanSubdomain(linkName)
        setFieldValue('subdomain', cleanedLinkName)
      }

      isNewHtmlFilesUploaded(files)
      return
    } else {
      const allExistingFiles = [existingFileSelected, ...existingFiles]

      for (const file of files) {
        const isDuplicate = allExistingFiles.some(
          (existingFile) => existingFile.name.toLowerCase() === file.name.toLowerCase()
        )

        if (isDuplicate) {
          alert(`File "${file.name}" already exists. Please upload files with different names.`)
          return
        }
      }

      const updatedFiles = [...existingFiles, ...files]
      const allFiles = [existingFileSelected, ...updatedFiles]

      setFieldValue('files', updatedFiles)
      setFieldValue('filesToAdd', allFiles)
    }
  }

  const onTemplateSelected = (setFieldValue, value) => setFieldValue('fileSelected', value)

  const onSubmit = async (
    { subdomain, fileSelected: initialFileSelected, email, indexFile, files, isZipPhpFile },
    authToken
  ) => {
    let fileSelected = initialFileSelected
    let currentDraggedFile = draggedFile

    if (fileSelected?.isRawFolder) {
      try {
        fileSelected = await zipFolderFilesWithWorker(fileSelected)
      } catch (error) {
        toast.error('Error compressing folder. Please try again.')
        return
      }
    }

    if (currentDraggedFile?.isRawFolder && !fileSelected) {
      try {
        currentDraggedFile = await zipFolderFilesWithWorker(currentDraggedFile)
      } catch (error) {
        toast.error('Error compressing folder. Please try again.')
        return
      }
    }

    const updatedSubdomain = cleanSubdomain(subdomain)
    const formData = new FormData()

    const template = fileSelected?.isTemplate
    const allFiles = [fileSelected, ...files].filter(Boolean)
    const containsRuntimeFile = allFiles.some((file) => isWasmerFile(file)) || isZipPhpFile

    if (containsRuntimeFile) {
      dispatch(
        showUpgradeCardModal({
          title: 'This upload type cannot be hosted on the free plan',
          desc: 'Please upgrade to upload PHP, Python, Node.js, or React ZIPs.',
          cta: 'Upgrade now',
          onClick: () => onUpgradePlan({ planId: TINY_PLAN.id }, dispatch)
        })
      )
      return
    }

    if (template) {
      formData.append('template', fileSelected.path)
    } else {
      if (files.length > 0) {
        formData.append('filesToAdd', fileSelected)
        files.forEach((file) => {
          formData.append('filesToAdd', file)
        })
      } else {
        const fileToUpload = fileSelected ? fileSelected : currentDraggedFile
        formData.append('filesToAdd', fileToUpload)
      }

      indexFile && formData.append('indexFile', indexFile)

      if (authToken) {
        formData.append('authToken', authToken)
      } else {
        formData.append('email', email?.toLowerCase())
      }
    }

    isZipPhpFile && formData.append('isZipPhpFile', isZipPhpFile)
    formData.append('domain', updatedSubdomain ? updatedSubdomain.toLowerCase() : '')
    formData.append('domainSuffix', domainSuffix)
    appendUtmParamsToFormData(formData)

    setRootState({ showLoading: true })

    const recaptchaValue = await googleReCaptchaProps.executeRecaptcha('homepage')
    const headers = {
      'x-recaptcha': recaptchaValue
    }

    const endpoint = `${API_ROOT}/v3/site/create/unverified`
    api
      .post(endpoint, formData, headers, null, false)
      .then((res) => {
        const { data } = res
        const classifierScanFiles = data?.classifierScanFiles
        const domain = data?.created

        try {
          classifierScan({ classifierScanFiles, domain, email }, headers)
        } catch (error) {
          console.error(error)
        }
        return res
      })
      .then((res) => {
        if (authToken) {
          history.push(`/free-welcome?id=${authToken}&trial=true`)
        } else {
          setRootState({
            showLoading: false,
            stage: template ? STAGE_SUCCESS : STAGE_SUCCESS_VERIFY,
            domain: res.data.created,
            recordedEmail: typeof res.data.recordedEmail === 'string' ? res.data.recordedEmail : email
          })
        }
      })
      .catch((err) => {
        const htmlFiles = err?.response?.data?.htmlFileList
        const isValidResponse = err?.response?.data?.code === errors.NO_INDEX_FILE && htmlFiles?.length > 0
        if (isValidResponse) {
          setHtmlFiles(htmlFiles)
          setShowFileList(isValidResponse)
        } else {
          setAuthToken(undefined)

          const errorMessage = getErrorMessage(err)
          if (errorMessage && errorMessage !== '') {
            setRootState({
              showLoading: false,
              showError: true,
              errorMessage
            })
          } else if (errorMessage && errorMessage === '') {
            setRootState({
              showLoading: false,
              showError: true,
              errorMessage: 'Oops, something went wrong'
            })
            console.error(err)
          } else {
            setRootState({
              showLoading: false,
              showError: true,
              errorMessage: 'Oops, something went wrong'
            })
            Sentry.captureException(err, {
              tags: {
                section: 'homepage-upload-error'
              }
            })
          }
        }
      })
  }

  const openWindow = (url) => window.open(url, '_blank')

  const openOAuthWindow = (provider) => openWindow(getOAuthUrl(provider, true))

  useEffect(() => {
    if (authToken) {
      onSubmit(formRef.current.values, authToken)
    }
  }, [authToken])

  const onMessageEvent = (event) => {
    if (event.data.type === 'oauthCallback') {
      if (event.data.newAccount) {
        setAuthToken(event.data.token)
      } else {
        setRootState({
          showLoading: false,
          showError: true,
          errorMessage: ERROR_MSGS[errors.USER_EXISTS]
        })
      }
    }
  }

  async function zipTestHTML(setFieldValue) {
    const ref = params.get('ref')
    if (ref === 'test-html') {
      const retrievedHTML = localStorage.getItem('html-code')
      const retrievedCSS = localStorage.getItem('css-code')
      const retrievedJS = localStorage.getItem('js-code')

      if (retrievedHTML) {
        const zip = new JSZip()
        zip.folder('html').file('index.html', retrievedHTML).file('main.css', retrievedCSS).file('main.js', retrievedJS)

        const content = await zip.generateAsync({ type: 'blob' })
        let file = new File([content], 'html.zip', {
          type: 'application/zip',
          lastModified: new Date()
        })
        file.path = 'html.zip'
        setFieldValue('fileSelected', file)
        setFieldValue('files', [])

        // clean up
        localStorage.removeItem('html-code')
        localStorage.removeItem('css-code')
        localStorage.removeItem('js-code')

        return file
      }
    } else if (ref === 'invite') {
      const addToCalConfig = localStorage.getItem('addToCalConfig')
      localStorage.removeItem('addToCalConfig')

      const htmlContent = getAddToCalHTML(JSON.parse(addToCalConfig))
      const inviteFile = new File([htmlContent], 'invite.html', {
        type: 'text/html',
        lastModified: new Date(),
        path: 'invite.html'
      })
      setFieldValue('fileSelected', inviteFile)

      return inviteFile
    } else if (ref === 'online-pdf-viewer') {
      try {
        const pdfFile = await getFileFromIndexedDB()
        if (pdfFile) {
          setFieldValue('fileSelected', pdfFile)
          setFieldValue('files', [])
          return pdfFile
        }
      } catch (error) {
        console.error('Error retrieving PDF from IndexedDB:', error)
      }
    }
  }

  // Define a function to handle adding more files
  const addMoreFiles = (setFieldValue, files, fileSelected) => {
    promptForAdditionalFiles({
      files,
      fileSelected,
      acceptedFilesType: DROPZONE_ACCEPTED_FILES_PRO,
      onFilesValidated: (validFilesToAdd) => {
        const updatedFiles = [...files, ...validFilesToAdd]
        setFieldValue('files', updatedFiles)
        isNewHtmlFilesUploaded([fileSelected, ...updatedFiles])
      },
      customValidation: (fileToAdd, allExistingFiles) => {
        if (!isValidFileSize(fileToAdd, MAX_FILE_SIZE_FREE)) {
          dispatch(
            showUpgradeCardModal({
              title: 'Upgrade to upload larger files',
              desc: `Files should be less than ${MAX_FILE_SIZE_FREE}MB on our free plan. File "${fileToAdd.name}" is too large.`,
              cta: 'Upload larger files',
              onClick: fileToAdd?.size ? () => onUpgradePlan({ fileSize: fileToAdd?.size }, dispatch) : null
            })
          )
          return false
        }

        if (!isTotalSizeWithinLimit(fileToAdd, allExistingFiles, MAX_FILE_SIZE)) {
          dispatch(
            showUpgradeCardModal({
              title: 'Upgrade to upload larger files',
              desc: `Total Files should be less than ${MAX_FILE_SIZE}MB on our free plan. Upgrade for more.`,
              cta: 'Upload larger files',
              onClick: fileToAdd?.size ? () => onUpgradePlan({ fileSize: fileToAdd?.size }, dispatch) : null
            })
          )
          return false
        }

        return true
      }
    })
  }

  const removeFile = (setFieldValue, files, fileSelected, isSelectedFile) => {
    removeFileFromUploadState({
      files,
      fileSelected: formRef.current?.values?.fileSelected,
      fileToRemove: fileSelected,
      isSelectedFile,
      onUpdateFiles: ({ newSelectedFile, newFiles }) => {
        if (isSelectedFile && newFiles.length > 0) {
          setFieldValue('fileSelected', newSelectedFile)
          setFieldValue('files', newFiles)
          setFieldValue('isZipPhpFile', false)
          setDomainSuffix(DOMAIN_SUFFIX)
          isNewHtmlFilesUploaded([newSelectedFile, ...newFiles])
        } else if (isSelectedFile) {
          setFieldValue('fileSelected', undefined)
          setFieldValue('files', [])
          setFieldValue('isZipPhpFile', false)
          dispatch(setDraggedFile(null))
          setDomainSuffix(DOMAIN_SUFFIX)
          setFieldValue('indexFile', undefined)
        } else {
          setFieldValue('files', newFiles)
          isNewHtmlFilesUploaded([newSelectedFile, ...newFiles])
        }
      }
    })
  }

  return (
    <>
      <Formik
        innerRef={formRef}
        validationSchema={schema}
        onSubmit={(values) => onSubmit(values)}
        initialValues={{
          fileSelected: undefined,
          subdomain: undefined,
          email: undefined,
          files: [],
          indexFile: undefined,
          isZipPhpFile: false
        }}
        validateOnBlur
      >
        {({ handleSubmit, handleBlur, handleChange, values, setFieldValue, touched, errors }) => (
          <form onSubmit={handleSubmit}>
            <InputGroup className="input-domain">
              <FormControl
                className="tr-landing-domain-input"
                name="subdomain"
                type="text"
                placeholder="link-name"
                aria-label="Subdomain"
                value={values.subdomain}
                onChange={handleChange}
                disabled={disabled}
                onBlur={handleBlur}
                isInvalid={touched.subdomain && errors.subdomain}
                maxLength={63}
              />
              <DropdownButton
                id="input-group-dropdown-2"
                variant="outline-primary"
                as={InputGroup.Append}
                title={domainSuffix}
              >
                <Dropdown.Item onClick={() => (window.location.hash = 'pricing')}>
                  <span style={{ fontSize: '14px', color: '#333' }}>
                    Add custom domain <img alt="Pro" src="/assets/icons/pro-black.png" height="20" />
                  </span>
                </Dropdown.Item>
              </DropdownButton>
            </InputGroup>
            {['test-html', 'invite', 'online-pdf-viewer'].includes(params.get('ref')) ||
            draggedFile ||
            values.fileSelected ? (
              <>
                <div className={`${values.files.length > 0 ? 'file-list' : ''}`}>
                  <SelectedFile
                    file={values.fileSelected || draggedFile || (async () => await zipTestHTML(setFieldValue))()}
                    indexFile={values.indexFile}
                    onSetAsIndex={(fileName) => setFieldValue('indexFile', fileName)}
                    onRemove={() => removeFile(setFieldValue, values.files, values.fileSelected, true)}
                    hideHomepageLabel={values.files.length === 1}
                  />
                  {values.files.map((file, index) => (
                    <SelectedFile
                      key={index}
                      file={file}
                      indexFile={values.indexFile}
                      onSetAsIndex={(fileName) => setFieldValue('indexFile', fileName)}
                      onRemove={() => removeFile(setFieldValue, values.files, file, false)}
                      hideHomepageLabel={values.files.length === 1}
                    />
                  ))}
                </div>
                {!isZipFile(values.fileSelected?.type || draggedFile?.type) &&
                  !isWasmerFile(values.fileSelected || draggedFile) &&
                  ![values.fileSelected, draggedFile, ...values.files].some((f) => f?.isFolder) && (
                    <Button
                      className="mb-3 mt-2 ms-auto d-block"
                      variant="outline-primary"
                      size="sm"
                      onClick={() => {
                        addMoreFiles(setFieldValue, values.files, values.fileSelected)
                      }}
                      data-testid="HomepageUpload-add-more-files"
                    >
                      + Add more files
                    </Button>
                  )}
                {!values.fileSelected?.isTemplate && !draggedFile?.isTemplate && (
                  <>
                    <div className="mb-3 mt-2">
                      <div className="bold font-20 color-purple-light">Please verify your email</div>
                      <span className="light-grey font-small">We need to verify your email to reduce spam</span>
                    </div>
                    <SocialLogin onClick={openOAuthWindow} className="height-50" disabled={errors.subdomain} />
                    <div className="separator mt-3 w-50 ms-auto me-auto">Or use a different email</div>
                    <div className="flex">
                      <div className="flex-2">
                        <FormControl
                          className="mt-3 tr-landing-email-input"
                          name="email"
                          type="text"
                          placeholder="Email"
                          aria-label="Email"
                          value={values.email}
                          onChange={handleChange}
                          disabled={disabled}
                        />
                        <ErrorMessage name="email" component="div" className="validation-error" />
                      </div>
                      <Button
                        type="submit"
                        className="flex-1 ms-2 mt-3 tr-landing-launch-site"
                        variant="outline-primary"
                        data-testid="HomepageUpload-continue-button"
                      >
                        Continue
                      </Button>
                    </div>
                  </>
                )}
                {values.fileSelected?.isTemplate && (
                  <Button
                    type="submit"
                    className="action-btn w-100 tr-landing-launch-site mt-2"
                    variant="purple"
                    data-testid="HomepageUpload-publish-button"
                  >
                    Publish
                  </Button>
                )}
              </>
            ) : (
              <>
                <ContentSelector
                  className="tr-landing-upload-file-zone"
                  type={contentType}
                  onFileSelect={onFileSelected}
                  onTemplateSelected={(template) => onTemplateSelected(setFieldValue, template)}
                  acceptedFiles={DROPZONE_ACCEPTED_FILES_PRO}
                  onShowCodeEditor={openCodeEditor}
                />
                <ErrorMessage
                  component="div"
                  name="fileSelected"
                  render={() => (
                    <div className="mt-1 validation-error text-center">Please upload a zip or single file</div>
                  )}
                />
              </>
            )}
            {htmlFiles && htmlFiles.length > 0 && (
              <FileListModal
                show={showFileList}
                onHide={() => setShowFileList(false)}
                onFileSelected={(file) => {
                  setFieldValue('indexFile', file)
                  setShowFileList(false)
                }}
                filesList={htmlFiles}
                title="Which file should be your homepage?"
                subtitle="We've found multiple HTML files"
              />
            )}
            {phpFiles?.length > 0 && (
              <FileListModal
                show={showFileList}
                onHide={() => setShowFileList(false)}
                onFileSelected={(file) => {
                  setFieldValue('indexFile', file)
                  setFieldValue('isZipPhpFile', true)
                  if (isWasmerFile(file)) {
                    setDomainSuffix('.tiiny.io')
                  }
                  setShowFileList(false)
                }}
                filesList={phpFiles}
                title="Which file should be your homepage?"
              />
            )}
            <CodeEditor
              show={showCodeEditor}
              onHide={closeCodeEditor}
              onSiteAction={() => {}}
              setDraggedFile={(file) => dispatch(setDraggedFile(file))}
              disableRouting={true}
            />
          </form>
        )}
      </Formik>
    </>
  )
}

const mapDispatchToProps = (dispatch) => ({ dispatch })

export default connect(undefined, mapDispatchToProps)(withRouter(withGoogleReCaptcha(HomepageUpload)))
